// Query-key factory + queryOptions for the BI SPA. One place so keys stay
// consistent between the components that read a query and the code that
// invalidates it, mirroring admin's `lib/queries.ts` convention. All fetchers
// throw `ApiError` (see lib/api.ts); components read `query.error` and branch
// on `ApiError.status`.
import { queryOptions } from "@tanstack/react-query";

import { activePreviewCatalog } from "./preview-catalog";
import { ApiError, fetchArtifacts, fetchBiGrants, fetchBiSource, fetchBiSourceValues, fetchBiSources, fetchChartResult, fetchCorpusBatches, fetchDashboard, fetchDashboards, fetchDrafts, fetchExperiments, fetchOnlineSchema, fetchProjectTags, fetchRunCompare, fetchRunCounts, fetchRunFindings, fetchStudioDashboard, fetchStudioDashboards, fetchStudioTemplate, fetchStudioVersions, fetchTagRuns, fetchTaggerSchema, fetchTaggers, fetchTaggingIdentity, fetchTags, searchBi } from "./api";

// THE CATALOG IS PART OF THE KEY. A preview and the environment's own catalog
// answer the same routes with different content, and `lib/cache-persist.ts`
// keeps this client's cache across reloads — so without the catalog in the key,
// opening a preview would render whatever the last ordinary session cached
// under the same key (and, worse, leave the preview's content there for the
// next ordinary one). It is constant for a page load (see
// lib/preview-catalog.ts), so this costs nothing at runtime; it is here so the
// two cannot occupy the same cache entry.
const catalog = activePreviewCatalog() ?? "env";

export const biKeys = {
  dashboards: ["bi", "dashboards", catalog] as const,
  dashboard: (id: string) => ["bi", "dashboard", catalog, id] as const,
  // Keyed by result key alone (not by chart/dashboard) — a `resultKey` is a
  // pure function of (dataset, metrics, dimensions, filters) per
  // `CompiledQuery.query_hash`, so two different charts that reduce to the
  // same query legitimately share one cache entry.
  result: (resultKey: string) => ["bi", "result", resultKey] as const,
};

/** The dashboard catalog. Also the app's boot identity probe — see
 *  lib/api.ts's `fetchDashboards` — so `App.tsx`'s `Boot()` and
 *  `pages/catalog.tsx` read the SAME query rather than costing two requests
 *  for "am I signed in" and "what dashboards exist". Read-mostly: dashboards
 *  are synced by an explicit resync action (`POST /sync`), not a live feed. */
export const dashboardsQuery = queryOptions({
  queryKey: biKeys.dashboards,
  queryFn: fetchDashboards,
  staleTime: 60_000,
  retry: false,
});

/** One dashboard's layout + filters + every ChartSpec it references. Stable
 *  once synced — a resync is the only thing that changes it, and that's a
 *  rare, explicit action a reader can retry into with a normal refetch. */
export function dashboardQuery(id: string) {
  return queryOptions({
    queryKey: biKeys.dashboard(id),
    queryFn: () => fetchDashboard(id),
    staleTime: 60_000,
  });
}

/** One chart result by its query-hash-derived key. Not fetched directly by
 *  pages/dashboard.tsx: lib/bi-query.ts owns request supersession and the
 *  inline-vs-Pusher fetch decision this query object backs. */
export function chartResultQuery(resultKey: string) {
  return queryOptions({
    queryKey: biKeys.result(resultKey),
    queryFn: () => fetchChartResult(resultKey),
    staleTime: 30_000,
  });
}

export const taggingKeys = {
  identity: ["tagging", "me"] as const,
  taggers: ["tagging", "taggers"] as const,
  tags: (tagger: string) => ["tagging", "tags", tagger] as const,
  runs: (tagger?: string) => ["tagging", "runs", tagger ?? "all"] as const,
  run: (runId: string) => ["tagging", "run", runId] as const,
  counts: (runId: string) => ["tagging", "counts", runId] as const,
  findings: (runId: string) => ["tagging", "findings", runId] as const,
  projectTags: (projectId: string) => ["tagging", "project", projectId] as const,
  corpus: ["tagging", "corpus"] as const,
  schema: (tagger: string) => ["tagging", "schema", tagger] as const,
  online: (tagger: string) => ["tagging", "online", tagger] as const,
  drafts: (tagger: string) => ["tagging", "drafts", tagger] as const,
  compare: (a: string, b: string, status: string) =>
    ["tagging", "compare", a, b, status] as const,
};

/** What this tagger asks in production. `null` until the first ingest: a 404
 *  here is a state the page renders ("nothing online yet"), not a failure. */
export function onlineSchemaQuery(tagger: string, enabled: boolean) {
  return queryOptions({
    queryKey: taggingKeys.online(tagger),
    queryFn: async () => {
      try {
        return await fetchOnlineSchema(tagger);
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }
    },
    enabled,
    staleTime: 60_000,
  });
}

export function draftsQuery(tagger: string, enabled: boolean) {
  return queryOptions({
    queryKey: taggingKeys.drafts(tagger),
    queryFn: () => fetchDrafts(tagger),
    enabled,
    staleTime: 15_000,
  });
}

/** Two finished runs compared. Both ids empty means nothing to ask yet. */
export function runCompareQuery(a: string, b: string, status: string) {
  return queryOptions({
    queryKey: taggingKeys.compare(a, b, status),
    queryFn: () => fetchRunCompare(a, b, { status: status || null }),
    enabled: Boolean(a && b && a !== b),
    staleTime: 60_000,
  });
}

/** The committed schema + the prompt it assembles to. Stable between deploys. */
export function taggerSchemaQuery(tagger: string, enabled: boolean) {
  return queryOptions({
    queryKey: taggingKeys.schema(tagger),
    queryFn: () => fetchTaggerSchema(tagger),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/** The uploaded corpora. Cheap and small — one row per batch, never the payloads. */
export const corpusQuery = queryOptions({
  queryKey: taggingKeys.corpus,
  queryFn: fetchCorpusBatches,
  staleTime: 30_000,
});

/** The caller's email + whether they hold `bi_site` at `full`. Read once and
 *  cached for the session: a grant does not change under a page. */
export const taggingIdentityQuery = queryOptions({
  queryKey: taggingKeys.identity,
  queryFn: fetchTaggingIdentity,
  staleTime: 5 * 60_000,
  retry: false,
});

/** The tagger roster, straight from the backend registry — which is why a new
 *  tagger needs no change here or in any component. */
export const taggersQuery = queryOptions({
  queryKey: taggingKeys.taggers,
  queryFn: fetchTaggers,
  staleTime: 60_000,
});

export function tagsQuery(tagger: string) {
  return queryOptions({
    queryKey: taggingKeys.tags(tagger),
    queryFn: () => fetchTags(tagger),
    staleTime: 30_000,
  });
}

/** Runs for one tagger. Polled only while a row is queued/running: the run is
 *  a worker sweep of minutes and has no Pusher event yet. */
export function tagRunsQuery(tagger?: string) {
  return queryOptions({
    queryKey: taggingKeys.runs(tagger),
    queryFn: () => fetchTagRuns(tagger),
    staleTime: 5_000,
    refetchInterval: (query) => {
      const rows = query.state.data;
      const live = rows?.some((r) => r.status === "queued" || r.status === "running");
      return live ? 5_000 : false;
    },
  });
}

export function runCountsQuery(runId: string) {
  return queryOptions({
    queryKey: taggingKeys.counts(runId),
    queryFn: () => fetchRunCounts(runId),
    staleTime: 30_000,
  });
}

export function runFindingsQuery(runId: string) {
  return queryOptions({
    queryKey: taggingKeys.findings(runId),
    queryFn: () => fetchRunFindings(runId),
    staleTime: 30_000,
  });
}

/** Everything currently believed about one project, from every tagger. */
export function projectTagsQuery(projectId: string) {
  return queryOptions({
    queryKey: taggingKeys.projectTags(projectId),
    queryFn: () => fetchProjectTags(projectId),
    staleTime: 30_000,
  });
}

/** The document list. Same cadence as the catalog: it changes on deploy. */
export const artifactsQuery = {
  queryKey: ["bi", "artifacts"] as const,
  queryFn: fetchArtifacts,
  staleTime: 5 * 60 * 1000,
};

export const studioKeys = {
  sources: ["studio", "sources"] as const,
  source: (id: string) => ["studio", "source", id] as const,
  values: (id: string, column: string) => ["studio", "values", id, column] as const,
  dashboards: ["studio", "dashboards"] as const,
  dashboard: (id: string) => ["studio", "dashboard", id] as const,
  versions: (id: string) => ["studio", "versions", id] as const,
  template: (slug: string) => ["studio", "template", slug] as const,
  experiments: ["studio", "experiments"] as const,
  search: (q: string) => ["studio", "search", q] as const,
};

/** The experiment register: what exists, and which sources carry each one.
 *
 *  `refetchInterval` only while the answer is `pending`, exactly as
 *  `biSourceValuesQuery` does and for the same reason: the first open of a cold
 *  environment enqueues the picker-value jobs and comes back with a short list,
 *  and without a second ask the page would say "no experiments" until somebody
 *  reloaded. A warm register costs nothing. */
export const experimentsQuery = queryOptions({
  queryKey: studioKeys.experiments,
  queryFn: fetchExperiments,
  staleTime: 60_000,
  refetchInterval: (query) => (query.state.data?.status === "pending" ? 4_000 : false),
});

/** One search. Disabled below two characters: a single letter matches most of
 *  the warehouse's column vocabulary, which is a long list nobody reads and a
 *  request per keystroke to produce it. */
export function biSearchQuery(query: string) {
  const trimmed = query.trim();
  return queryOptions({
    queryKey: studioKeys.search(trimmed),
    queryFn: () => searchBi(trimmed),
    enabled: trimmed.length >= 2,
    staleTime: 30_000,
  });
}

/** Everything the canvas may build over. One Postgres read behind it, and the
 *  list moves only when `bi_source_sync` finds a new view, so this is cached for
 *  as long as a page lives. */
export const biSourcesQuery = queryOptions({
  queryKey: studioKeys.sources,
  queryFn: fetchBiSources,
  staleTime: 5 * 60_000,
});

/** One source with its full column list — the canvas's left rail. */
export function biSourceQuery(sourceId: string) {
  return queryOptions({
    queryKey: studioKeys.source(sourceId),
    queryFn: () => fetchBiSource(sourceId),
    enabled: Boolean(sourceId),
    staleTime: 5 * 60_000,
  });
}

/** A select control's options.
 *
 *  `refetchInterval` while the answer is `pending`, and ONLY then: a cold read
 *  enqueues the distinct-values job and comes back empty, so without a second
 *  ask the control would show "loading" until the reader reloaded the page.
 *  This is the same "poll only while something is actually in flight" shape
 *  `tagRunsQuery` uses, for the same reason — a warm control costs nothing. */
export function biSourceValuesQuery(sourceId: string, column: string, enabled = true) {
  return queryOptions({
    queryKey: studioKeys.values(sourceId, column),
    queryFn: () => fetchBiSourceValues(sourceId, column),
    enabled: enabled && Boolean(sourceId && column),
    staleTime: 60_000,
    refetchInterval: (query) => (query.state.data?.status === "pending" ? 3_000 : false),
  });
}

/** The caller's own dashboards plus every shared one. Also carries the caller's
 *  real signed-in email, which the canvas needs for the Pusher channel exactly
 *  as the authored catalog does. */
export const studioDashboardsQuery = queryOptions({
  queryKey: studioKeys.dashboards,
  queryFn: fetchStudioDashboards,
  staleTime: 30_000,
});

export function studioDashboardQuery(id: string) {
  return queryOptions({
    queryKey: studioKeys.dashboard(id),
    queryFn: () => fetchStudioDashboard(id),
    enabled: Boolean(id),
    // No staleTime: this is the document an editor is about to write over, and
    // a save's optimistic version check is only as good as the version the
    // editor loaded. Re-reading on focus is how a second tab notices.
    staleTime: 0,
  });
}

/** A template by slug. A 404 resolves to `null` rather than throwing: "no
 *  template with that slug in this environment yet" is a state the page renders,
 *  the same shape `onlineSchemaQuery` uses for its own not-yet-ingested case. */
export function studioTemplateQuery(slug: string) {
  return queryOptions({
    queryKey: studioKeys.template(slug),
    queryFn: async () => {
      try {
        return await fetchStudioTemplate(slug);
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }
    },
    enabled: Boolean(slug),
    staleTime: 60_000,
  });
}

export function studioVersionsQuery(id: string, enabled = true) {
  return queryOptions({
    queryKey: studioKeys.versions(id),
    queryFn: () => fetchStudioVersions(id),
    enabled: enabled && Boolean(id),
    staleTime: 10_000,
  });
}

export const permissionKeys = {
  grants: ["bi", "grants"] as const,
};

/** The BI access list. `retry: false` because the expected failure here is a
 *  403 (the caller holds `read`, not `full`), and retrying an authorization
 *  answer three times only delays the page that explains it. Short staleTime:
 *  this is a list somebody edits and immediately re-reads. */
export const biGrantsQuery = queryOptions({
  queryKey: permissionKeys.grants,
  queryFn: fetchBiGrants,
  staleTime: 15_000,
  retry: false,
});
